import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fmtError, BACKEND_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  BadgeCheck, Star, Trophy, LogOut, Settings as Cog, Crown, Zap, Sparkles, ShieldCheck, Heart, Edit3, ArrowLeft,
  PartyPopper, Award, Users, Coins as CoinsIcon, MessageCircle, Calendar as CalIcon, Eye, Camera, Upload, Loader2, X,
  Gift as GiftIcon, ShoppingBag, Grid3x3,
} from "lucide-react";
import FriendButton from "@/components/app/FriendButton";
import PhotoViewer from "@/components/app/PhotoViewer";
import { useTranslation } from "react-i18next";

const ICONS = { Sparkles, PartyPopper, Trophy, Crown, Star, Award };

// Country flag emoji + localized country name (auto-detected from the user's
// online IP by the backend — never user-editable).
const ccFlag = (cc) =>
  String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)));
const regionName = (cc, lang) => {
  try { return new Intl.DisplayNames([lang], { type: "region" }).of(cc.toUpperCase()) || cc; }
  catch { return cc; }
};
const INTEREST_PRESETS = ["coffee", "gym", "hiking", "gaming", "cooking", "music", "books", "movies", "travel", "yoga", "tech", "art", "dance", "soccer", "tennis"];
const BACKEND = BACKEND_BASE;
const MAX_AVATAR_BYTES = 10 * 1024 * 1024;

export default function Profile() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const targetId = id || user?.id;
  const isMe = !id || id === user?.id;
  const [target, setTarget] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [showGiftPicker, setShowGiftPicker] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [showIdModal, setShowIdModal] = useState(false);
  const [showPhotoViewer, setShowPhotoViewer] = useState(false);

  const load = async () => {
    if (!targetId) return;
    try {
      const { data } = await api.get(isMe ? "/auth/me" : `/users/${targetId}`);
      setTarget(data);
      setDraft({
        name: data.name || "",
        age: data.age || 18,
        bio: data.bio || "",
        avatar_url: data.avatar_url || "",
        interests: data.interests || [],
        energy_level: data.energy_level || "balanced",
        location: data.location || "",
      });
      if (isMe) {
        const { data: ach } = await api.get("/achievements/me");
        setAchievements(ach);
      }
      try {
        const { data: g } = await api.get(`/users/${targetId}/gifts`);
        setGifts(g);
      } catch (e) { console.warn("Failed to load gifts:", e); }
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [targetId]);

  // Auto-refresh while an AI ID review is pending — Gemini takes 10-15s. Stop
  // polling as soon as the AI finishes (status flips to approved/rejected, or
  // the AI sub-object reports done/error). Polls own profile only.
  useEffect(() => {
    if (!isMe) return undefined;
    const sub = target?.id_submission;
    const isPolling = sub?.status === "pending" && (!sub?.ai || sub.ai.status === "pending");
    if (!isPolling) return undefined;
    const handle = setInterval(() => { load(); }, 4000);
    return () => clearInterval(handle);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isMe, target?.id_submission?.status, target?.id_submission?.ai?.status]);

  if (!target) return <div className="p-8 text-center">{t("common.loading")}</div>;

  const save = async () => {
    try {
      await api.patch("/users/me", { ...draft, age: Number(draft.age) });
      toast.success(t("profile.saved"));
      setEditing(false);
      await refresh();
      load();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
  };

  const toggleReady = async () => {
    try {
      await api.post("/users/me/spontaneous", { is_ready: !target.is_ready_now });
      toast.success(!target.is_ready_now ? t("profile.youReReady") : t("profile.standDown"));
      load();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };

  const goVip = () => nav("/vip");
  const block = async () => {
    if (!confirm(t("profile.blockConfirm", { name: target.name }))) return;
    try { await api.post(`/users/${target.id}/block`); toast.success(t("profile.blocked")); nav("/feed"); } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };
  const startDM = async () => {
    try {
      const { data } = await api.post(`/chats/direct/${target.id}`);
      nav(`/chats/${data.id}`);
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 pb-32">
      {!isMe && (
        <button onClick={() => nav(-1)} className="btn-nb mb-5" data-testid="back-btn"><ArrowLeft className="w-4 h-4" strokeWidth={3}/> Back</button>
      )}
      {(() => {
        const eq = target.equipped_expanded || {};
        const banner = eq.banner?.data;
        const theme = eq.profile_theme?.data;
        const effectName = eq.profile_effect?.data?.effect;
        const nameStyle = eq.name_style?.data;
        const ring = eq.avatar_decoration?.data;
        const badge = eq.name_badge?.data;

        const effectClass = { sparkle: "fx-sparkle", pulse: "fx-pulse", confetti: "fx-confetti", aurora: "fx-aurora" }[effectName] || "";
        const cardStyle = theme ? { background: theme.bg, color: theme.text } : undefined;
        const cardClass = theme ? "" : "bg-[#FFF0D4]";
        const nameInner = nameStyle ? (
          <span className={`name-gradient ${nameStyle.animate ? "animated" : ""}`} style={{ backgroundImage: nameStyle.gradient }}>
            {target.name}
          </span>
        ) : target.name;

        return (
          <>
            {banner && (
              <div
                className="profile-banner"
                style={{ background: banner.background }}
                data-testid="profile-banner"
              />
            )}
            <div className={`card-nb p-6 mb-5 relative ${cardClass} ${effectClass}`} style={cardStyle} data-testid="profile-card">
              <div className="flex items-start gap-4 relative z-10">
                {isMe ? (
                  <button
                    type="button"
                    onClick={() => nav("/profile/edit")}
                    data-testid="profile-avatar-edit"
                    className="relative w-24 h-24 rounded-full flex items-center justify-center font-display font-black text-4xl shadow-nb-sm overflow-hidden shrink-0 group cursor-pointer bg-white border-2 border-[#121212]"
                    style={ring ? {
                      boxShadow: ring.glow ? `0 0 18px ${ring.color || "#22d3ee"}, 4px 4px 0 #121212` : "4px 4px 0 #121212",
                      outline: `${ring.thickness || 4}px solid transparent`,
                      outlineOffset: "0px",
                      background: ring.gradient || ring.color || undefined,
                    } : undefined}
                    aria-label={t("profile.changePicture")}
                  >
                    <span className="absolute inset-[3px] rounded-full overflow-hidden bg-white border-2 border-[#121212] flex items-center justify-center">
                      {target.avatar_url
                        ? <img src={target.avatar_url} alt="" className="w-full h-full object-cover"/>
                        : target.name?.[0]?.toUpperCase()}
                    </span>
                    <span className="absolute inset-0 bg-[#0A0B0D]/55 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-full">
                      <Camera className="w-7 h-7 text-white" strokeWidth={2.25}/>
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowPhotoViewer(true)}
                    data-testid="profile-avatar-view"
                    className="w-24 h-24 rounded-full flex items-center justify-center font-display font-black text-4xl shadow-nb-sm overflow-hidden shrink-0 bg-white border-2 border-[#121212] relative cursor-pointer hover:opacity-90 transition-opacity"
                    style={ring ? {
                      boxShadow: ring.glow ? `0 0 18px ${ring.color || "#22d3ee"}, 4px 4px 0 #121212` : "4px 4px 0 #121212",
                      background: ring.gradient || ring.color || undefined,
                    } : undefined}
                    aria-label={t("profile.viewProfile")}
                  >
                    <span className="absolute inset-[3px] rounded-full overflow-hidden bg-white border-2 border-[#121212] flex items-center justify-center">
                      {target.avatar_url ? <img src={target.avatar_url} alt="" className="w-full h-full object-cover"/> : target.name?.[0]?.toUpperCase()}
                    </span>
                  </button>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="font-display font-black text-3xl truncate" data-testid="profile-name">{nameInner}</h1>
                    {target.verified && <BadgeCheck className="w-6 h-6 text-[#00B4D8]" strokeWidth={3}/>}
                    {target.vip && <Crown className="w-6 h-6 text-[#F9C349]" strokeWidth={3} fill="#F9C349"/>}
                    {badge && (
                      <span className="name-badge" style={{ background: (badge.color || "#000") + "22", borderColor: badge.color }} data-testid="profile-badge">
                        <span>{badge.icon}</span> {badge.label}
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-bold text-[#0F9D8F] tracking-tight -mt-0.5 mb-1" data-testid="profile-username">
                    @{target.username || target.name?.toLowerCase().replace(/[^a-zA-Z0-9]/g, "_").replace(/_+/g, "_").replace(/^_+|_+$/g, "") || "user"}
                  </p>
                  <p className="text-sm" style={theme ? { color: theme.text, opacity: .75 } : { color: "#4A4A4A" }} data-testid="profile-meta">
                    {target.age} · {["male","female","other"].includes(target.gender) ? t(`createActivity.${target.gender}`) : (target.gender || "—")}
                    {target.country_code
                      ? <> · <span data-testid="profile-country">{ccFlag(target.country_code)} {regionName(target.country_code, i18n.language)}</span>{target.location ? ` · ${target.location}` : ""}</>
                      : <> · {target.location || t("profile.anywhere")}</>}
                  </p>
                  <div className="flex gap-3 mt-2 text-sm font-bold flex-wrap">
                    <span className="flex items-center gap-1"><Star className="w-4 h-4 text-[#F9C349]" fill="#F9C349"/> {target.rating_avg?.toFixed(1) || "—"} ({target.ratings_count || 0})</span>
                    <span className="flex items-center gap-1"><Trophy className="w-4 h-4"/> {target.completed_count || 0} {t("profile.done")}</span>
                    <span className="flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-[#00B4D8]"/> {t("profile.trust")} {target.reliability_score ?? 100}</span>
            </div>
          </div>
        </div>
        {target.bio && <p className="mt-3 text-[#121212] whitespace-pre-wrap">{target.bio}</p>}

        {/* Mood emojis */}
        {target.mood_emojis?.length > 0 && (
          <div className="flex gap-2 flex-wrap mt-3" data-testid="profile-mood-emojis">
            {target.mood_emojis.map((e, i) => (
              <span key={i} className="inline-flex items-center justify-center w-9 h-9 text-2xl bg-[#FFF6E0] rounded-xl">{e}</span>
            ))}
          </div>
        )}

        {/* Languages */}
        {target.languages?.length > 0 && (
          <div className="mt-3" data-testid="profile-languages">
            <div className="text-xs font-display font-bold uppercase tracking-widest text-[#7C828D] mb-1.5">{t("profileEdit.languages")}</div>
            <div className="flex gap-2 flex-wrap">
              {target.languages.map((lang, i) => {
                const palette = ["bg-[#E879F9]","bg-[#9D5DFF]","bg-[#A78BFA]","bg-[#F472B6]","bg-[#22D3EE]","bg-[#60A5FA]"];
                return (
                  <span
                    key={lang}
                    className={`${palette[i % palette.length]} text-black font-black text-xs px-2.5 py-1 rounded-md uppercase tracking-wide`}
                    style={{ transform: `rotate(${(i % 2 === 0 ? -1 : 1) * (1 + (i % 3))}deg)` }}
                    data-testid={`profile-lang-${lang}`}
                  >
                    {lang}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Tags / interests */}
        {target.interests?.length > 0 && (
          <div className="mt-3" data-testid="profile-tags">
            <div className="text-xs font-display font-bold uppercase tracking-widest text-[#7C828D] mb-1.5">{t("profileEdit.tags")}</div>
            <div className="flex gap-2 flex-wrap">
              {target.interests.map((tag, i) => {
                const palette = ["bg-[#00CED1]","bg-[#FF7B6B]","bg-[#9D5DFF]","bg-[#22D3EE]","bg-[#34D399]","bg-[#FF8AB6]","bg-[#FB923C]","bg-[#60A5FA]"];
                return (
                  <span
                    key={tag}
                    className={`${palette[i % palette.length]} text-black font-black text-xs px-2.5 py-1 rounded-md uppercase tracking-wide`}
                    style={{ transform: `rotate(${(i % 2 === 0 ? -1 : 1) * (1 + (i % 3))}deg)` }}
                    data-testid={`profile-tag-${tag}`}
                  >
                    {tag}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Profession */}
        {target.professions?.length > 0 && (
          <div className="mt-3" data-testid="profile-professions">
            <div className="text-xs font-display font-bold uppercase tracking-widest text-[#7C828D] mb-1.5">{t("profileEdit.profession")}</div>
            <div className="space-y-1">
              {target.professions.map((p, i) => (
                <div key={i} className="text-sm font-semibold">{p}</div>
              ))}
            </div>
          </div>
        )}

        {/* Education */}
        {(target.education_level || target.university) && (
          <div className="mt-3" data-testid="profile-education">
            <div className="text-xs font-display font-bold uppercase tracking-widest text-[#7C828D] mb-1.5">{t("profileEdit.education")}</div>
            {target.education_level && (
              <div className="text-sm font-semibold capitalize">{target.education_level.replace(/_/g, " ")}</div>
            )}
            {target.university && (
              <div className="text-sm text-[#3D424B]">{target.university}</div>
            )}
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          {isMe ? (
            <>
              <button onClick={toggleReady} className={`btn-nb ${target.is_ready_now ? "btn-nb-warn" : ""}`} data-testid="spontaneous-toggle">
                <Zap className="w-4 h-4" strokeWidth={3}/> {target.is_ready_now ? t("profile.readyNow") : t("profile.imReadyNow")}
              </button>
              <button onClick={() => nav("/profile/edit")} className="btn-nb btn-nb-info" data-testid="edit-profile-btn"><Edit3 className="w-4 h-4" strokeWidth={3}/> {t("common.edit")}</button>
              {!target.verified && (() => {
                const sub = target.id_submission;
                const status = sub?.status;
                if (status === "pending") {
                  const aiPending = sub?.ai?.status === "pending" || !sub?.ai;
                  return (
                    <button
                      disabled
                      className="btn-nb btn-nb-warn opacity-90 inline-flex items-center gap-1.5"
                      data-testid="verify-pending"
                    >
                      {aiPending
                        ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={3} />
                        : <BadgeCheck className="w-4 h-4" strokeWidth={3} />}
                      {aiPending ? t("profile.aiReviewing") : t("profile.idUnderReview")}
                    </button>
                  );
                }
                // ai_failed = automated verification technically failed (budget,
                // image load, model timeout). Show it as a retryable error, not
                // a rejection — the user's ID wasn't actually reviewed.
                if (status === "ai_failed") {
                  return (
                    <button
                      onClick={() => setShowIdModal(true)}
                      className="btn-nb btn-nb-danger inline-flex items-center gap-1.5"
                      data-testid="verify-retry-btn"
                    >
                      <BadgeCheck className="w-4 h-4" strokeWidth={3}/> {t("profile.idRetry")}
                    </button>
                  );
                }
                const label = status === "rejected" ? t("profile.resubmitId") : t("profile.verifyId");
                return <button onClick={() => setShowIdModal(true)} className="btn-nb" data-testid="verify-btn"><BadgeCheck className="w-4 h-4" strokeWidth={3}/> {label}</button>;
              })()}
              {!target.vip && <button onClick={goVip} className="btn-nb btn-nb-warn" data-testid="vip-btn"><Crown className="w-4 h-4" strokeWidth={3}/> {t("profile.goVip")}</button>}
              <button onClick={() => nav("/settings")} className="btn-nb" data-testid="settings-btn"><Cog className="w-4 h-4" strokeWidth={3}/> {t("settings.title")}</button>
            </>
          ) : (
            <>
              <FriendButton userId={target.id} status={target.friend_status} onChange={load}/>
              <button onClick={startDM} className="btn-nb btn-nb-info" data-testid="dm-btn"><MessageCircle className="w-4 h-4" strokeWidth={2.5}/> {t("profile.message")}</button>
              <button onClick={() => setShowGiftPicker(true)} className="btn-nb btn-nb-warn" data-testid="send-gift-btn"><GiftIcon className="w-4 h-4" strokeWidth={2.5}/> {t("gifts.send")}</button>
              <button onClick={block} className="btn-nb" data-testid="block-btn">{t("profile.block")}</button>
            </>
          )}
        </div>
      </div>
            </>
          );
        })()}

      {/* Photos strip — Tanta horizontal carousel; tap any to open the full PhotoViewer */}
      {(() => {
        const list = (target.photos && target.photos.length > 0)
          ? target.photos
          : (target.avatar_url ? [target.avatar_url] : []);
        if (list.length <= 1) return null;  // single photo is already the avatar
        return (
          <div className="mb-5" data-testid="profile-photos-strip">
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
              {list.map((url, i) => (
                <button
                  key={url + i}
                  onClick={() => setShowPhotoViewer(true)}
                  className="snap-start shrink-0 w-[112px] h-[150px] rounded-2xl overflow-hidden border-2 border-[#121212] shadow-nb-sm hover:-translate-y-0.5 transition-all"
                  data-testid={`profile-photo-${i}`}
                  aria-label="Open photos"
                >
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Quick-access tiles for own profile */}
      {isMe && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5" data-testid="profile-tiles">
          <Link to="/coins" data-testid="tile-coins" className="card-nb p-4 bg-gradient-to-br from-[#FFF1D6] to-[#FFE08A] hover:-translate-y-0.5 transition-all">
            <CoinsIcon className="w-6 h-6 mb-1" strokeWidth={2.25} fill="#F9C349"/>
            <div className="font-display font-extrabold text-lg leading-tight">{target.coins ?? 0}</div>
            <div className="text-[11px] text-[#4A3000] font-semibold">{t("profile.tCoins")}</div>
          </Link>
          <Link to="/profile/shop" data-testid="tile-shop" className="card-nb p-4 bg-gradient-to-br from-[#FFE6E0] to-[#FFB6A8] hover:-translate-y-0.5 transition-all">
            <ShoppingBag className="w-6 h-6 mb-1" strokeWidth={2.25}/>
            <div className="font-display font-extrabold text-lg leading-tight">{t("shop.shortTitle", "Shop")}</div>
            <div className="text-[11px] text-[#8B2C1A] font-semibold">{t("shop.shortSubtitle", "Customise profile")}</div>
          </Link>
          <Link to="/vip" data-testid="tile-vip" className="card-nb p-4 text-white hover:-translate-y-0.5 transition-all" style={{ background: target.is_vip ? "linear-gradient(135deg, #1F2228, #0F1115)" : "linear-gradient(135deg, #4A4F58, #1F2228)" }}>
            <Crown className="w-6 h-6 mb-1 text-[#F9C349]" strokeWidth={2.25} fill="#F9C349"/>
            <div className="font-display font-extrabold text-lg leading-tight">{target.is_vip ? "VIP" : t("profile.goVip")}</div>
            <div className="text-[11px] text-white/70 font-semibold">{target.is_vip && target.vip_until ? t("profile.untilDate", { date: new Date(target.vip_until).toLocaleDateString() }) : t("profile.unlockPerks")}</div>
          </Link>
          <Link to="/friends" data-testid="tile-friends" className="card-nb p-4 bg-gradient-to-br from-[#DDF5EA] to-[#BFE9D6] hover:-translate-y-0.5 transition-all">
            <Users className="w-6 h-6 mb-1" strokeWidth={2.25}/>
            <div className="font-display font-extrabold text-lg leading-tight">{target.friends?.length ?? 0}</div>
            <div className="text-[11px] text-[#0F9D8F] font-semibold">{t("profile.friendsCount")}</div>
          </Link>
          <Link to="/calendar" data-testid="tile-calendar" className="card-nb p-4 bg-gradient-to-br from-[#ECE6FF] to-[#D9CFFF] hover:-translate-y-0.5 transition-all">
            <CalIcon className="w-6 h-6 mb-1" strokeWidth={2.25}/>
            <div className="font-display font-extrabold text-lg leading-tight">{target.completed_count ?? 0}</div>
            <div className="text-[11px] text-[#5B45B0] font-semibold">{t("profile.pastActivities")}</div>
          </Link>
          <Link to="/me/posts" data-testid="tile-posts" className="card-nb p-4 bg-gradient-to-br from-[#FFF5D6] to-[#F9C349] hover:-translate-y-0.5 transition-all">
            <Grid3x3 className="w-6 h-6 mb-1" strokeWidth={2.25}/>
            <div className="font-display font-extrabold text-lg leading-tight">{target.posts_count ?? 0}</div>
            <div className="text-[11px] text-[#7A5500] font-semibold">{t("profile.posts", { defaultValue: "Posts" })}</div>
          </Link>
        </div>
      )}

      {/* Premium gifts received */}
      {gifts.length > 0 && (
        <div className="card-nb p-6 mb-5 bg-gradient-to-br from-[#FFF7E0] to-[#FFE08A]" data-testid="gifts-card">
          <h3 className="font-display font-black text-xl mb-1 flex items-center gap-2">
            <GiftIcon className="w-5 h-5"/> {t("gifts.received")}
            <span className="tag-nb bg-white !py-0 !px-2 text-xs">{gifts.length}</span>
          </h3>
          <p className="text-xs text-[#4A3000] mb-3">{t("gifts.receivedSubtitle")}</p>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
            {gifts.slice(0, 18).map((g) => (
              <div
                key={g.id}
                title={`${g.name} · from ${g.from_name}`}
                className="aspect-square flex flex-col items-center justify-center p-1 rounded-xl bg-white/70 border border-[#121212]/10 hover:bg-white transition-colors"
                data-testid={`gift-${g.id}`}
              >
                <span className="text-3xl leading-none" aria-label={g.name}>{g.emoji}</span>
                <span className="text-[9px] uppercase tracking-wider font-bold text-[#4A3000] mt-0.5 truncate w-full text-center">{g.from_name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Achievements (own only) */}
      {isMe && (
        <div className="card-nb p-6 mb-5 bg-white" data-testid="achievements-card">
          <h3 className="font-display font-black text-xl mb-3 flex items-center gap-2"><Award className="w-5 h-5"/> {t("profile.realBadges")}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {achievements.map((a) => {
              const Icon = ICONS[a.icon] || Sparkles;
              return (
                <div key={a.id} className={`p-3 border-2 border-[#121212] rounded-2xl text-center ${a.earned ? "bg-[#E2F3E4] shadow-nb-sm" : "bg-[#F4F1EA] opacity-60"}`} data-testid={`badge-${a.id}`}>
                  <Icon className={`w-8 h-8 mx-auto mb-1 ${a.earned ? "" : "opacity-40"}`} strokeWidth={2.5}/>
                  <div className="font-display font-black text-sm">{t(`achievements.${a.id}.name`, a.title)}</div>
                  <div className="text-[10px] text-[#4A4A4A]">{t(`achievements.${a.id}.desc`, a.desc)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ID-submit modal */}
      {showIdModal && (
        <IdSubmitModal
          onClose={() => setShowIdModal(false)}
          rejectReason={
            (target.id_submission?.status === "rejected" || target.id_submission?.status === "ai_failed")
              ? target.id_submission.reject_reason
              : ""
          }
          aiFailed={target.id_submission?.status === "ai_failed"}
          accountName={target.name}
          onSubmitted={() => { setShowIdModal(false); load(); }}
        />
      )}

      {/* Send-gift modal */}
      {showGiftPicker && !isMe && (
        <GiftPickerModal
          target={target}
          ownCoins={user?.coins ?? 0}
          onClose={() => setShowGiftPicker(false)}
          onSent={() => { setShowGiftPicker(false); load(); refresh(); }}
        />
      )}

      {/* Tanta photo viewer (own + other users) */}
      {showPhotoViewer && (
        <PhotoViewer
          photos={(target.photos && target.photos.length > 0) ? target.photos : (target.avatar_url ? [target.avatar_url] : [])}
          name={target.name}
          age={target.age}
          location={target.location}
          verified={target.verified}
          vip={target.vip}
          onClose={() => setShowPhotoViewer(false)}
        />
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-[#121212]/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setEditing(false)}>
          <div className="card-nb p-6 max-w-md w-full bg-white animate-pop max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-black text-xl mb-3">{t("profile.editProfile") || t("profile.edit")}</h3>
            <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("profile.name")}</label>
            <input className="input-nb mb-3" value={draft.name} onChange={(e) => setDraft({...draft, name: e.target.value})} data-testid="edit-name"/>
            <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("auth.age")}</label>
            <input type="number" className="input-nb mb-3" value={draft.age} onChange={(e) => setDraft({...draft, age: e.target.value})} data-testid="edit-age"/>
            <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("profile.location")}</label>
            <input className="input-nb mb-3" value={draft.location} onChange={(e) => setDraft({...draft, location: e.target.value})} data-testid="edit-location"/>
            <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("profile.bio")}</label>
            <textarea className="input-nb mb-3 min-h-[80px]" maxLength={300} value={draft.bio} onChange={(e) => setDraft({...draft, bio: e.target.value})} data-testid="edit-bio"/>
            <label className="block text-xs font-display font-bold uppercase tracking-widest mb-2">{t("profile.profilePicture")}</label>
            <AvatarPicker
              currentUrl={draft.avatar_url}
              fallbackInitial={draft.name?.[0]?.toUpperCase()}
              onChange={(url) => setDraft({ ...draft, avatar_url: url })}
            />
            <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1 mt-4">{t("profile.energy")}</label>
            <select className="input-nb mb-3" value={draft.energy_level} onChange={(e) => setDraft({...draft, energy_level: e.target.value})} data-testid="edit-energy">
              <option value="energetic">{t("profile.energetic")}</option>
              <option value="calm">{t("profile.calm")}</option>
              <option value="balanced">{t("profile.balanced")}</option>
            </select>
            <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("profile.interests")}</label>
            <div className="flex flex-wrap gap-2 mb-4">
              {INTEREST_PRESETS.map((t) => {
                const active = draft.interests.includes(t);
                return (
                  <button key={t} type="button" onClick={() => setDraft({...draft, interests: active ? draft.interests.filter(x => x !== t) : [...draft.interests, t]})}
                    className={`tag-nb cursor-pointer ${active ? "bg-[#FF6B6B] text-white" : "bg-white"}`} data-testid={`int-${t}`}>
                    {active ? "✓" : "+"} {t}
                  </button>
                );
              })}
            </div>
            <button onClick={save} className="btn-nb btn-nb-primary w-full" data-testid="save-profile-btn">{t("common.save")}</button>
          </div>
        </div>
      )}

      {/* Suggestions */}
      {isMe && <MatchSuggestions />}
    </div>
  );
}

function AvatarPicker({ currentUrl, fallbackInitial, onChange }) {
  const { t } = useTranslation();
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("profile.pickImage"));
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      toast.error(t("profile.imageTooLarge"));
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    setProgress(0);
    try {
      const { data } = await api.post("/uploads/media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      const absolute = data.url.startsWith("http") ? data.url : `${BACKEND}${data.url}`;
      onChange(absolute);
      toast.success(t("profile.uploaded"));
    } catch (err) {
      toast.error(fmtError(err.response?.data?.detail) || t("profile.uploadFailed"));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className="mb-3" data-testid="avatar-picker">
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
        data-testid="avatar-gallery-input"
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="user"
        className="hidden"
        onChange={handleFile}
        data-testid="avatar-camera-input"
      />
      <div className="flex items-center gap-4">
        <div className="relative w-20 h-20 rounded-full bg-white border-2 border-[#121212] flex items-center justify-center font-display font-black text-2xl overflow-hidden shrink-0">
          {currentUrl
            ? <img src={currentUrl} alt="" className="w-full h-full object-cover"/>
            : <span>{fallbackInitial || "?"}</span>}
          {uploading && (
            <div className="absolute inset-0 bg-[#0A0B0D]/60 flex flex-col items-center justify-center text-white">
              <Loader2 className="w-5 h-5 animate-spin"/>
              <span className="text-[10px] font-bold mt-0.5">{progress}%</span>
            </div>
          )}
        </div>
        <div className="flex-1 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => galleryRef.current?.click()}
            className="btn-nb btn-nb-primary"
            disabled={uploading}
            data-testid="avatar-pick-gallery"
          >
            <Upload className="w-4 h-4" strokeWidth={2.5}/> {t("profile.gallery")}
          </button>
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            className="btn-nb btn-nb-info"
            disabled={uploading}
            data-testid="avatar-pick-camera"
          >
            <Camera className="w-4 h-4" strokeWidth={2.5}/> {t("profile.camera")}
          </button>
          {currentUrl && !uploading && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="col-span-2 text-xs text-[#7C828D] underline hover:text-[#0A0B0D] mt-0.5"
              data-testid="avatar-remove"
            >
              {t("profile.removePicture")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function MatchSuggestions() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [vipRequired, setVipRequired] = useState(false);
  useEffect(() => {
    api.get("/match/suggestions").then((r) => {
      if (Array.isArray(r.data)) {
        setUsers(r.data); setVipRequired(false);
      } else {
        setUsers(r.data?.suggestions || []);
        setVipRequired(!!r.data?.vip_required);
      }
    }).catch(() => {});
  }, []);

  if (vipRequired) {
    return (
      <div className="card-nb p-6 bg-gradient-to-br from-[#FFE08A] to-[#F9C349] relative overflow-hidden" data-testid="match-suggestions-locked">
        <h3 className="font-display font-black text-xl mb-2 flex items-center gap-2">
          <Heart className="w-5 h-5 text-[#FF6B6B]"/> {t("profile.vibeMatches")}
          <Crown className="w-4 h-4 text-[#4A3000] ml-1" strokeWidth={2.75}/>
        </h3>
        <p className="text-sm text-[#4A3000] font-display font-semibold mb-4 max-w-md">
          {t("profile.vibeMatchesSubtitle")}
        </p>
        <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-3 select-none pointer-events-none" aria-hidden>
          {[0, 1].map((i) => (
            <div key={i} className="flex items-center gap-3 p-3 bg-white border-2 border-[#121212] rounded-2xl">
              <div className="w-10 h-10 rounded-full bg-[#FFF0D4] border-2 border-[#121212] blur-md"/>
              <div className="flex-1 min-w-0">
                <div className="h-3 w-2/3 bg-[#E5E2D6] rounded mb-1 blur-sm"/>
                <div className="h-2 w-1/2 bg-[#E5E2D6] rounded blur-sm"/>
              </div>
              <span className="tag-nb bg-[#F9C349] blur-sm">★ ★</span>
            </div>
          ))}
        </div>
        <Link
          to="/vip"
          className="btn-nb btn-nb-primary mt-4 inline-flex"
          data-testid="match-suggestions-vip-cta"
        >
          <Crown className="w-4 h-4" strokeWidth={2.75}/> {t("profile.goVipUnlock")}
        </Link>
      </div>
    );
  }

  if (!users.length) return null;
  return (
    <div className="card-nb p-6 bg-[#EBE4FF]" data-testid="match-suggestions">
      <h3 className="font-display font-black text-xl mb-3 flex items-center gap-2"><Heart className="w-5 h-5 text-[#FF6B6B]"/> {t("profile.vibeMatches")}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {users.slice(0, 6).map((u) => (
          <Link key={u.id} to={`/u/${u.id}`} className="flex items-center gap-3 p-3 bg-white border-2 border-[#121212] rounded-2xl">
            <div className="w-10 h-10 rounded-full bg-[#FFF0D4] border-2 border-[#121212] flex items-center justify-center font-display font-black overflow-hidden">
              {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-full h-full object-cover"/> : u.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold truncate">{u.name}</div>
              <div className="text-xs text-[#4A4A4A] truncate">{u.shared_interests?.slice(0,3).map(x => `#${x}`).join(" ")}</div>
            </div>
            <span className="tag-nb bg-[#F9C349]">{u.match_score} ★</span>
          </Link>
        ))}
      </div>
    </div>
  );
}


const ID_DOC_TYPES = [
  { id: "passport", labelKey: "profile.passport" },
  { id: "drivers_license", labelKey: "profile.driversLicense" },
  { id: "id_card", labelKey: "profile.idCard" },
  { id: "residence_permit", labelKey: "profile.residencePermit" },
];

function IdSubmitModal({ onClose, rejectReason, aiFailed, accountName, onSubmitted }) {
  const { t } = useTranslation();
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    document_url: "",
    document_type: "passport",
    full_name: accountName || "",
    date_of_birth: "",
  });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error(t("profile.pickImage")); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error(t("profile.imageTooLarge")); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    setProgress(0);
    try {
      const { data } = await api.post("/uploads/media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => { if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100)); },
      });
      const absolute = data.url.startsWith("http") ? data.url : `${BACKEND}${data.url}`;
      setForm((f) => ({ ...f, document_url: absolute }));
      toast.success(t("profile.docUploaded"));
    } catch (err) {
      toast.error(fmtError(err.response?.data?.detail) || t("profile.uploadFailed"));
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.document_url) { toast.error(t("profile.uploadIdFirst")); return; }
    if (!form.full_name.trim()) { toast.error(t("profile.enterLegalName")); return; }
    if (!form.date_of_birth) { toast.error(t("profile.enterDob")); return; }
    setBusy(true);
    try {
      await api.post("/users/me/id-submit", form);
      toast.success(t("profile.submitted"));
      onSubmitted();
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || t("errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#0A0B0D]/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="card-nb p-6 max-w-md w-full bg-white animate-pop max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="id-submit-modal">
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-display font-black text-xl">{t("profile.idVerifyTitle")}</h3>
          <button onClick={onClose} className="btn-nb" data-testid="id-modal-close"><X className="w-4 h-4" strokeWidth={2.5}/></button>
        </div>
        <p className="text-sm text-[#4A4F58] mb-4">
          {t("profile.idVerifyBody")}
        </p>

        {rejectReason && (
          <div
            className={`rounded-2xl p-3 mb-4 text-sm ${
              aiFailed
                ? "bg-[#FFF3CD] text-[#8B6914] border-2 border-[#F9C349]"
                : "bg-[#FFE6E0] text-[#D14848]"
            }`}
            data-testid={aiFailed ? "ai-failed-note" : "reject-reason-note"}
          >
            <strong>{aiFailed ? t("profile.aiFailedTitle") : t("profile.prevRejection")}:</strong>{" "}
            {rejectReason}
            {!aiFailed && ` ${t("profile.addressResubmit")}`}
          </div>
        )}

        <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="id-gallery-input"/>
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} data-testid="id-camera-input"/>

        {!form.document_url && !uploading && (
          <div className="grid grid-cols-2 gap-3 mb-4">
            <button type="button" onClick={() => galleryRef.current?.click()} className="btn-nb btn-nb-primary py-5 flex-col" data-testid="id-pick-gallery">
              <Upload className="w-5 h-5" strokeWidth={2.5}/><span className="font-display font-bold text-sm">{t("profile.gallery")}</span>
            </button>
            <button type="button" onClick={() => cameraRef.current?.click()} className="btn-nb btn-nb-info py-5 flex-col" data-testid="id-pick-camera">
              <Camera className="w-5 h-5" strokeWidth={2.5}/><span className="font-display font-bold text-sm">{t("profile.camera")}</span>
            </button>
          </div>
        )}

        {uploading && (
          <div className="rounded-2xl border border-[#0F1115]/10 bg-[#F4F2ED] p-4 mb-4 text-center">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-1 text-[#14C8B0]" strokeWidth={2.5}/>
            <p className="text-sm font-display font-bold">{t("profile.uploading")} {progress}%</p>
          </div>
        )}

        {form.document_url && !uploading && (
          <div className="relative rounded-2xl overflow-hidden border border-[#0F1115]/10 bg-[#F4F2ED] mb-4">
            <img src={form.document_url} alt={t("profile.idDocAlt")} className="w-full max-h-60 object-contain"/>
            <button onClick={() => setForm({ ...form, document_url: "" })} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-[#0A0B0D]/80 text-white flex items-center justify-center" data-testid="id-clear">
              <X className="w-4 h-4" strokeWidth={2.5}/>
            </button>
          </div>
        )}

        <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("profile.docType")}</label>
        <select className="input-nb mb-3" value={form.document_type} onChange={(e) => setForm({ ...form, document_type: e.target.value })} data-testid="id-doc-type">
          {ID_DOC_TYPES.map((dt) => <option key={dt.id} value={dt.id}>{t(dt.labelKey)}</option>)}
        </select>

        <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("profile.fullNameId")}</label>
        <input className="input-nb mb-3" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="id-full-name"/>

        <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("profile.dob")}</label>
        <input type="date" className="input-nb mb-4" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="id-dob"/>

        <button onClick={submit} disabled={busy || uploading} className="btn-nb btn-nb-primary w-full" data-testid="id-submit-btn">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> {t("profile.submitting")}</> : t("profile.submitReview")}
        </button>
        <p className="text-[11px] text-[#7C828D] mt-3 text-center">{t("profile.secureNote")}</p>
      </div>
    </div>
  );
}

function GiftPickerModal({ target, ownCoins, onClose, onSent }) {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const RARITY_BG = {
    common: "linear-gradient(135deg,#F4F1EA,#E8E3D6)",
    uncommon: "linear-gradient(135deg,#DDF5EA,#BFE9D6)",
    rare: "linear-gradient(135deg,#D8EEFF,#A8D8FF)",
    epic: "linear-gradient(135deg,#ECE6FF,#D9CFFF)",
    legendary: "linear-gradient(135deg,#FFE6E0,#FF9C8A)",
  };

  useEffect(() => {
    api.get("/gifts/catalog").then((r) => {
      setCatalog(r.data.gifts || []);
    }).catch((e) => toast.error(fmtError(e.response?.data?.detail) || e.message));
  }, []);

  const send = async () => {
    if (!selected) { toast.error(t("gifts.pickFirst")); return; }
    if (ownCoins < selected.cost_coins) { toast.error(t("gifts.notEnough")); return; }
    setBusy(true);
    try {
      await api.post("/gifts/send", { to_user_id: target.id, gift_id: selected.id, message: message.trim() || null });
      toast.success(t("gifts.sentToast", { emoji: selected.emoji, name: selected.name, who: target.name }));
      onSent();
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-[#0A0B0D]/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="card-nb p-6 max-w-lg w-full bg-white animate-pop max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="gift-picker">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-display font-black text-xl tracking-tight">{t("gifts.title")}</h3>
            <p className="text-sm text-[#4A4F58]">{t("gifts.subtitle", { name: target.name })}</p>
          </div>
          <button onClick={onClose} className="btn-nb" data-testid="gift-close"><X className="w-4 h-4" strokeWidth={2.5}/></button>
        </div>

        <div className="flex items-center justify-between text-xs font-display font-bold uppercase tracking-widest text-[#7C828D] mb-2">
          <span>{t("gifts.yourBalance")}</span>
          <span className="text-[#0F1115]">{ownCoins} 🪙</span>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-4">
          {catalog.map((g) => {
            const tooExpensive = ownCoins < g.cost_coins;
            const isSelected = selected?.id === g.id;
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => setSelected(g)}
                disabled={tooExpensive}
                className={`p-2 rounded-2xl border-2 flex flex-col items-center justify-center aspect-square transition-all
                  ${isSelected ? "border-[#0F1115] shadow-nb-sm -translate-y-0.5" : "border-[#121212]/15"}
                  ${tooExpensive ? "opacity-40" : "hover:-translate-y-0.5"}`}
                style={{ background: RARITY_BG[g.rarity] || RARITY_BG.common }}
                data-testid={`gift-opt-${g.id}`}
              >
                <span className="text-3xl leading-none">{g.emoji}</span>
                <span className="text-[10px] font-display font-bold mt-1 truncate w-full text-center">{g.name}</span>
                <span className="text-[10px] text-[#4A4F58]">{g.cost_coins} 🪙</span>
              </button>
            );
          })}
        </div>

        {selected && (
          <div className="rounded-2xl bg-[#FFF7E0] border border-[#F9C349] p-3 mb-3">
            <div className="text-sm font-display font-bold">
              {selected.emoji} {selected.name} · {selected.cost_coins} 🪙
            </div>
          </div>
        )}

        <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("gifts.message")}</label>
        <textarea
          className="input-nb mb-3 min-h-[60px]"
          maxLength={160}
          placeholder={t("gifts.messagePh")}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          data-testid="gift-message"
        />

        <button
          onClick={send}
          disabled={busy || !selected || ownCoins < (selected?.cost_coins ?? 0)}
          className="btn-nb btn-nb-primary w-full"
          data-testid="gift-send-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <GiftIcon className="w-4 h-4" strokeWidth={2.5}/>}
          {selected
            ? t("gifts.sendCta", { name: selected.name, cost: selected.cost_coins })
            : t("gifts.pickFirst")}
        </button>
        <p className="text-[10px] text-[#7C828D] mt-3 text-center italic" data-testid="premium-gift-fee-note">
          * {t("coinsPage.serviceFeeIncluded")}
        </p>
      </div>
    </div>
  );
}
